'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAppSelector } from '@/store/hook';

type WithAuthOptions = {
  redirectTo?: string;
  fallback?: React.ReactNode;
};

// ⭐ P extends object 避免 TS 覺得 P 可能是原始型別
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: WithAuthOptions
): React.FC<P> {
  const ComponentWithAuth: React.FC<P> = (props) => {
    const router = useRouter();
    const token = useAppSelector((state) => state.auth.token);
    const hydrated = useAppSelector((state) => state.auth.hydrated);

    const redirectTo = options?.redirectTo ?? '/auth/login';
    const fallback = options?.fallback ?? null;

    useEffect(() => {
      if (!hydrated) return;
      if (!token) {
        router.replace(redirectTo);
      }
    }, [hydrated, token, router, redirectTo]);

    if (!hydrated || !token) {
      return <>{fallback}</>;
    }

    // ⭐ 這一行是關鍵：props 型別會是 P，WrappedComponent 也吃 P
    return <WrappedComponent {...(props as P)} />;
  };

  // 讓 DevTools 看得出來是誰被包起來（可有可無）
  ComponentWithAuth.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return ComponentWithAuth;
}
